/**
 * Resolves location data (coordinates or address) from a data object
 * based on schema field definitions.
 *
 * Returns: { type, data, error }
 *   type:  "markers" | "address" | null
 *   data:  marker array or address string
 *   error: { code, message } or null
 */

const JUNK_VALUES = new Set(["n/a", "na", "none", "null", "undefined", "unknown", "-", "--", "---", ".", "..", "test", "tbd", "xxx"]);

const ADDRESS_EXCLUDE_PATTERNS = ["email", "mail", "e_mail", "ip_addr", "mac_addr", "url", "uri", "web"];

const ERROR_CODES = {
    MASKED_DATA: { code: "MASKED_DATA", message: "Location data is masked. Unmask the data to view the location." },
    EMPTY_VALUE: { code: "EMPTY_VALUE", message: "Location field exists but has no value for this record." },
    INVALID_COORDINATES: { code: "INVALID_COORDINATES", message: "Coordinates are invalid or out of range." },
    NULL_ISLAND: { code: "NULL_ISLAND", message: "Coordinates point to 0°, 0° which is likely a default/missing value." },
    JUNK_VALUE: { code: "JUNK_VALUE", message: "Location field contains a placeholder value." },
    INSUFFICIENT_PARTS: { code: "INSUFFICIENT_PARTS", message: "Not enough address components to determine a location." },
    NO_FIELDS: { code: "NO_FIELDS", message: "No location field found. Cannot show location details." },
};

// Field Finders

const findByDataType = (fields, dataTypes) => {
    return fields.find((field) => dataTypes.some((type) => field.dataType.toLowerCase() === type.toLowerCase()));
};

const findByClassifier = (fields, classifierTags) => {
    return fields.find((field) => field.classifiers && field.classifiers.some((obj) => classifierTags.some((tag) => obj["tagName"].toLowerCase() === tag.toLowerCase())));
};

const findByName = (fields, patterns, excludePatterns = []) => {
    const candidates = fields.filter((field) => {
        const name = field.name.toLowerCase();
        return !excludePatterns.some((ex) => name.includes(ex));
    });

    // 1. Exact match
    for (const pattern of patterns) {
        const match = candidates.find((f) => f.name.toLowerCase() === pattern.toLowerCase());
        if (match) return match;
    }

    // 2. Segment match (split by _ - . space)
    for (const pattern of patterns) {
        const match = candidates.find((f) => {
            const segments = f.name.toLowerCase().split(/[_\-\s.]+/);
            return segments.includes(pattern.toLowerCase());
        });
        if (match) return match;
    }

    // 3. Starts with (only patterns >= 4 chars)
    for (const pattern of patterns) {
        if (pattern.length < 4) continue;
        const match = candidates.find((f) => f.name.toLowerCase().startsWith(pattern.toLowerCase()));
        if (match) return match;
    }

    // 4. Substring (only patterns >= 4 chars)
    return candidates.find((f) => patterns.filter((p) => p.length >= 4).some((p) => f.name.toLowerCase().includes(p.toLowerCase())));
};

// Value Validators

const isMasked = (val) => String(val).includes("####");
const isJunk = (val) => JUNK_VALUES.has(String(val).toLowerCase().trim());
const isEmpty = (val) => val == null || String(val).trim() === "";

const validateLatLng = (latVal, lngVal) => {
    if (isEmpty(latVal) || isEmpty(lngVal)) return { ok: false, error: ERROR_CODES.EMPTY_VALUE };
    if (isMasked(latVal) || isMasked(lngVal)) return { ok: false, error: ERROR_CODES.MASKED_DATA };

    const lat = parseFloat(String(latVal).trim());
    const lng = parseFloat(String(lngVal).trim());

    if (isNaN(lat) || isNaN(lng)) return { ok: false, error: ERROR_CODES.INVALID_COORDINATES };
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return { ok: false, error: ERROR_CODES.INVALID_COORDINATES };
    if (lat === 0 && lng === 0) return { ok: false, error: ERROR_CODES.NULL_ISLAND };

    return {
        ok: true,
        data: [{ position: { latitude: lat.toString(), longitude: lng.toString() } }],
    };
};

const validateGeocode = (value) => {
    if (isEmpty(value)) return { ok: false, error: ERROR_CODES.EMPTY_VALUE };
    if (isMasked(value)) return { ok: false, error: ERROR_CODES.MASKED_DATA };

    const str = String(value).trim();
    const parts = str.split(",");
    if (parts.length !== 2) return { ok: false, error: ERROR_CODES.INVALID_COORDINATES };

    return validateLatLng(parts[0].trim(), parts[1].trim());
};

const validateAddress = (value) => {
    if (isEmpty(value)) return { ok: false, error: ERROR_CODES.EMPTY_VALUE };
    if (isMasked(value)) return { ok: false, error: ERROR_CODES.MASKED_DATA };

    const str = String(value).trim();
    if (str.length < 3) return { ok: false, error: ERROR_CODES.JUNK_VALUE };
    if (isJunk(str)) return { ok: false, error: ERROR_CODES.JUNK_VALUE };

    return { ok: true, data: str };
};

//Resolution Strategies

const resolveCoordinates = (fields, dataObject) => {
    const strategies = [
        // DataType (confidence 3)
        {
            confidence: 3,
            find: () => {
                const lat = findByDataType(fields, ["latitude"]);
                const lng = findByDataType(fields, ["longitude"]);
                return lat && lng ? { type: "latlng", lat, lng } : null;
            },
        },
        {
            confidence: 3,
            find: () => {
                const geo = findByDataType(fields, ["latlng", "geocode"]);
                return geo ? { type: "geocode", field: geo } : null;
            },
        },
        // Classifier (confidence 2)
        {
            confidence: 2,
            find: () => {
                const lat = findByClassifier(fields, ["latitude", "geolatitude"]);
                const lng = findByClassifier(fields, ["longitude", "geolongitude"]);
                return lat && lng ? { type: "latlng", lat, lng } : null;
            },
        },
        {
            confidence: 2,
            find: () => {
                const geo = findByClassifier(fields, ["geocode", "geocoordinates", "coordinates"]);
                return geo ? { type: "geocode", field: geo } : null;
            },
        },
        // Name (confidence 1)
        {
            confidence: 1,
            find: () => {
                const lat = findByName(fields, ["latitude", "lat"], ADDRESS_EXCLUDE_PATTERNS);
                const lng = findByName(fields, ["longitude", "long", "lng"], ADDRESS_EXCLUDE_PATTERNS);
                return lat && lng ? { type: "latlng", lat, lng } : null;
            },
        },
        {
            confidence: 1,
            find: () => {
                const geo = findByName(fields, ["geocode", "geocoordinates", "coordinates", "coords", "latlng", "latlong"], ADDRESS_EXCLUDE_PATTERNS);
                return geo ? { type: "geocode", field: geo } : null;
            },
        },
    ];

    let firstError = null;

    for (const strategy of strategies) {
        const found = strategy.find();
        if (!found) continue;

        const validated = found.type === "latlng" ? validateLatLng(dataObject[found.lat.fieldId], dataObject[found.lng.fieldId]) : validateGeocode(dataObject[found.field.fieldId]);

        if (validated.ok) {
            return { type: "markers", data: validated.data, confidence: strategy.confidence, error: null };
        }

        if (!firstError) firstError = validated.error;
    }

    return firstError ? { type: null, data: null, confidence: 0, error: firstError } : null;
};

const resolveAddress = (fields, dataObject) => {
    const strategies = [
        // Classifier (confidence 2)
        {
            confidence: 2,
            find: () => findByClassifier(fields, ["physical_address", "mailing_address", "street_address", "home_address", "address", "location"]),
        },
        // Name (confidence 1)
        {
            confidence: 1,
            find: () =>
                findByName(
                    fields,
                    [
                        "full_address",
                        "physical_address",
                        "mailing_address",
                        "street_address",
                        "home_address",
                        "residential_address",
                        "postal_address",
                        "address",
                        "location",
                        "addr",
                    ],
                    ADDRESS_EXCLUDE_PATTERNS,
                ),
        },
    ];

    let bestResult = null;
    let firstError = null;

    // Composed address check
    const composed = composeAddress(fields, dataObject);
    if (composed.ok && composed.partsCount >= 2) {
        // If it found 3 or more components, it's very reliable. Give it highest confidence.
        bestResult = { type: "address", data: composed.data, confidence: composed.partsCount >= 3 ? 2.5 : 1.5, error: null };
    } else if (composed.error) {
        firstError = composed.error;
    }

    for (const strategy of strategies) {
        const field = strategy.find();
        if (!field) continue;

        const validated = validateAddress(dataObject[field.fieldId]);

        if (validated.ok) {
            // Only override if confidence is strictly greater
            if (!bestResult || strategy.confidence > bestResult.confidence) {
                bestResult = { type: "address", data: validated.data, confidence: strategy.confidence, error: null };
            }
        } else {
            if (!firstError) firstError = validated.error;
        }
    }

    if (bestResult) {
        return bestResult;
    }

    return firstError ? { type: null, data: null, confidence: 0, error: firstError } : null;
};

const composeAddress = (fields, dataObject) => {
    const components = [
        { patterns: ["building", "bldg", "unit", "suite", "apartment", "apt", "address_line_2", "addressline2", "address line 2", "address2", "address 2", "addr2"] },
        { patterns: ["street", "road", "st", "rd", "address_line_1", "addressline1", "address line 1", "address1", "address 1", "addr1", "street_address"] },
        { patterns: ["locality", "area", "neighborhood", "suburb"] },
        { patterns: ["city", "town", "municipality"] },
        { patterns: ["state", "province", "region", "district"] },
        { patterns: ["country", "nation"] },
        { patterns: ["zip", "zipcode", "postal_code", "postcode", "pin_code", "pincode", "postalcode"] },
    ];

    let hasMasked = false;
    let hasEmpty = false;
    const parts = [];

    for (const component of components) {
        const field = findByClassifier(fields, component.patterns) || findByName(fields, component.patterns, ADDRESS_EXCLUDE_PATTERNS);

        if (!field) continue;

        const value = dataObject[field.fieldId];

        if (isEmpty(value)) {
            hasEmpty = true;
            continue;
        }
        if (isMasked(value)) {
            hasMasked = true;
            continue;
        }

        const str = String(value).trim();
        if (isJunk(str)) continue;

        parts.push(str);
    }

    if (hasMasked) {
        const message =
            parts.length > 0
                ? `Only a partial address could be extracted (${parts.join(", ")}). Please unmask the remaining address components to view the full location on the map.`
                : ERROR_CODES.MASKED_DATA.message;
        return { ok: false, error: { code: "MASKED_DATA", message }, partsCount: parts.length };
    }
    if (parts.length >= 2) return { ok: true, data: parts.join(", "), partsCount: parts.length };
    if (hasEmpty) return { ok: false, error: ERROR_CODES.EMPTY_VALUE, partsCount: parts.length };
    if (parts.length === 1) return { ok: false, error: ERROR_CODES.INSUFFICIENT_PARTS, partsCount: parts.length };

    return { ok: false, error: null, partsCount: 0 };
};

/**
 * @param {Array} fields - Schema model fields
 * @param {Object} dataObject - Selected data object
 * @returns {{ type: "markers"|"address"|null, data: any, error: {code, message}|null }}
 */
export const resolveLocation = (fields, dataObject) => {
    if (!fields || !dataObject) {
        return { type: null, data: null, error: ERROR_CODES.NO_FIELDS };
    }

    const coordResult = resolveCoordinates(fields, dataObject);
    const addrResult = resolveAddress(fields, dataObject);

    const coordOk = coordResult && coordResult.type !== null;
    const addrOk = addrResult && addrResult.type !== null;

    // Both valid → use confidence
    if (coordOk && addrOk) {
        return coordResult.confidence >= addrResult.confidence ? coordResult : addrResult;
    }

    // One valid
    if (coordOk) return coordResult;
    if (addrOk) return addrResult;

    // Both failed → return the most informative error
    const error = (coordResult && coordResult.error) || (addrResult && addrResult.error) || ERROR_CODES.NO_FIELDS;
    return { type: null, data: null, error };
};

export { ERROR_CODES };
